import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useMemo } from "react";

import { ListLoadingState } from "../../components/ListLoadingState";
import { VisitFiltersBottomSheet } from "../Filters/BottomSheet/Sheet";
import { filterVisits, getPerVisitPay } from "../Filters/filterVisits";
import { type VisitFilters } from "../Filters/types";
import { useVisitFiltersContext } from "../Filters/useVisitFiltersContext";
import { useGetWorkerVisitLockStatus } from "../useGetWorkerVisitLockStatus";
import { useVisitsListData } from "../useVisitsListData";
import { InHomeOpenVisitCard } from "./Card";
import { InHomeOpenVisitsEmptyState } from "./EmptyState";
import { InHomeLockBanner } from "./InHomeLockBanner";

interface InHomeOpenVisitsListProps {
  filterModalState: UseModalState;
}

export function InHomeOpenVisitsList(props: InHomeOpenVisitsListProps) {
  const { filterModalState } = props;
  const { userId: workerId } = useDefinedWorker();

  const {
    visits,
    caseIdToPatientMap,
    caseIdToWorkplaceNameMap,
    caseIdToWorkplaceIdMap,
    caseIdToSpecialtiesMap,
    isVisitsLoading,
    isVisitsSuccess,
  } = useVisitsListData({ booked: "false" });

  const { distance, shiftRoles, excludedCareTypes, visitTypes, payMinimum, visitsPerWeekMaximum } =
    useVisitFiltersContext();

  const datasetPayMin = useMemo(() => {
    const payValues = visits.map(getPerVisitPay).filter(isDefined);
    return payValues.length > 0 ? Math.min(...payValues) : 0;
  }, [visits]);

  const filteredVisits = useMemo(
    () =>
      filterVisits({
        visits,
        caseIdToSpecialtiesMap,
        caseIdToPatientMap,
        filters: {
          distance,
          shiftRoles,
          excludedCareTypes,
          visitTypes,
          payMinimum,
          visitsPerWeekMaximum,
        },
      }),
    [
      visits,
      caseIdToSpecialtiesMap,
      caseIdToPatientMap,
      distance,
      shiftRoles,
      excludedCareTypes,
      visitTypes,
      payMinimum,
      visitsPerWeekMaximum,
    ]
  );

  const getFilteredVisitsCount = useCallback(
    (filters: Partial<VisitFilters>) =>
      filterVisits({ visits, caseIdToSpecialtiesMap, caseIdToPatientMap, filters }).length,
    [visits, caseIdToSpecialtiesMap, caseIdToPatientMap]
  );

  const { data: lockResponse } = useGetWorkerVisitLockStatus(workerId);
  const lockedByWorkplaceId = lockResponse?.data?.attributes.lockedByWorkplaceId;

  if (isVisitsLoading) {
    return (
      <ListLoadingState
        texts={[
          "Finding you the best-paying visits nearby",
          "Analyzing visit opportunities just for you",
          "Your perfect visit is just a moment away",
          "Crunching the numbers to maximize your earnings",
          "Searching high and low for the best visits nearby",
          "We are on it - finding visits that matter most",
          "Unlocking your next great opportunity",
        ]}
      />
    );
  }

  const emptyState = isDefined(lockedByWorkplaceId) ? (
    <InHomeLockBanner lockedByWorkplaceId={lockedByWorkplaceId} />
  ) : (
    <InHomeOpenVisitsEmptyState unfilteredVisitsCount={visits.length} />
  );

  const visitsList =
    isVisitsSuccess && filteredVisits.length === 0 ? (
      emptyState
    ) : (
      <Stack spacing={5}>
        {isDefined(lockedByWorkplaceId) && (
          <InHomeLockBanner lockedByWorkplaceId={lockedByWorkplaceId} />
        )}
        {filteredVisits.map((visit) => {
          const patient = caseIdToPatientMap.get(visit.attributes.caseId);
          const specialties = caseIdToSpecialtiesMap.get(visit.attributes.caseId);
          const workplaceName = caseIdToWorkplaceNameMap.get(visit.attributes.caseId);
          const workplaceId = caseIdToWorkplaceIdMap.get(visit.attributes.caseId);

          return (
            <InHomeOpenVisitCard
              key={visit.id}
              visit={visit}
              patient={patient}
              specialties={specialties ?? []}
              workplaceName={workplaceName ?? ""}
              workplaceId={workplaceId ?? ""}
            />
          );
        })}
      </Stack>
    );

  return (
    <>
      {visitsList}
      <VisitFiltersBottomSheet
        modalState={filterModalState}
        getFilteredVisitsCount={getFilteredVisitsCount}
        datasetPayMin={datasetPayMin}
      />
    </>
  );
}
